import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  mlaDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  fullDate,
  get,
  groupDigits,
  italic,
  type LegalValues,
  mlaPageRange,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * U.S. presidential executive orders in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 §11.7, "Administrative and Executive Materials" (Publication Manual pp.
 *   365–366), as reproduced by Liberty University's APA 7 legal table and the Texas
 *   Medical Center library: "Exec. Order No. xxxxx, 3 C.F.R. Page (Year). URL" —
 *   "Exec. Order No. 13,676, 3 C.F.R. 294 (2014)." In text: (Exec. Order No. 13,676,
 *   2014); narrative: Executive Order No. 13,676 (2014). Orders not yet compiled in
 *   title 3 are cited to the Federal Register, which APA's template for uncodified
 *   rules abbreviates "F.R." with the full date.
 * - Bluebook 22d ed. rules 14.7 and 6.2 (Cornell LII, Basic Legal Citation §2-480):
 *   "Exec. Order No. 14,080, 87 Fed. Reg. 52,847 (Aug. 30, 2022)." — the Federal
 *   Register date, not the signing date; or the C.F.R. compilation year. Five-digit
 *   numbers take commas.
 * - Chicago 18 §14.190 (presidential documents), which follows the Bluebook from
 *   §14.170; legal documents are cited in notes, not the bibliography (Portland, Lone
 *   Star and Menlo CMOS guides). Chicago spells out the month.
 * - MLA 9: MLA Style Center, "How do I cite an executive order by a US president?"
 *   (25 June 2025) — "United States, Executive Office of the President [Donald Trump].
 *   Executive Order 14255: Establishing the United States Investment Accelerator. 31
 *   Mar. 2025. Federal Register, vol. 90, no. 63, 3 Apr. 2025, pp. 14701–702, www…";
 *   in text (United States, Executive Order 14255). Title in roman, Federal Register in
 *   italics, no comma in the order number.
 */

export const EXECUTIVE_ORDER_FIELDS = {
  number: "number",
  title: "title",
  president: "president",
  signed: "signed",
  frVolume: "frVolume",
  frIssue: "frIssue",
  frPage: "frPage",
  frLastPage: "frLastPage",
  frDate: "frDate",
  cfrPage: "cfrPage",
  cfrYear: "cfrYear",
  url: "url",
} as const;

const F = EXECUTIVE_ORDER_FIELDS;

export interface ExecutiveOrderLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Order {
  /** As typed: "13676". */
  number: string;
  /** Bluebook digit grouping: "13,676". */
  legalNumber: string;
  title: string;
  president: string;
  signed: string;
  fr: {
    volume: string;
    issue: string;
    page: string;
    lastPage: string;
    date: ReturnType<typeof parsePartialDate>;
  } | null;
  cfr: { page: string; year: string } | null;
  url: string;
}

const RE_NON_DIGITS = /\D/g;

function readOrder(values: LegalValues): Order {
  const number = get(values, F.number).replace(RE_NON_DIGITS, "");
  const frVolume = get(values, F.frVolume);
  const frPage = get(values, F.frPage);
  const cfrPage = get(values, F.cfrPage);
  const cfrYear = get(values, F.cfrYear);
  return {
    number,
    legalNumber: groupDigits(number),
    title: get(values, F.title),
    president: get(values, F.president),
    signed: get(values, F.signed),
    fr:
      frVolume && frPage
        ? {
            volume: frVolume,
            issue: get(values, F.frIssue),
            page: frPage,
            lastPage: get(values, F.frLastPage),
            date: parsePartialDate(get(values, F.frDate)),
          }
        : null,
    cfr: cfrPage && cfrYear ? { page: cfrPage, year: cfrYear } : null,
    url: get(values, F.url),
  };
}

/** APA prefers the title 3 compilation; the Federal Register stands in until it exists. */
function apaSource(order: Order): { source: string; year: string } {
  if (order.cfr) {
    return {
      source: `3 C.F.R. ${order.cfr.page} (${order.cfr.year})`,
      year: order.cfr.year,
    };
  }
  const fr = order.fr;
  const date = fr ? fullDate(fr.date) : "";
  return {
    source: fr ? `${fr.volume} F.R. ${fr.page}${date ? ` (${date})` : ""}` : "",
    year: fr?.date ? String(fr.date.year) : "n.d.",
  };
}

function apaOutput(order: Order, labels: ExecutiveOrderLabels): CitationOutput {
  const { source, year } = apaSource(order);
  const head = `Exec. Order No. ${order.legalNumber}`;
  const reference = `${source ? `${head}, ${source}` : head}.${
    order.url ? ` ${order.url}` : ""
  }`;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: [plain(reference)] },
      { label: labels.parenthetical, parts: [plain(`(${head}, ${year})`)] },
      {
        label: labels.narrative,
        parts: [plain(`Executive Order No. ${order.legalNumber} (${year})`)],
      },
    ],
    note: labels.apaNote,
  };
}

/** "Federal Register, vol. 79, no. 184, 23 Sept. 2014, pp. 56931–35" */
function mlaContainer(order: Order): CitationParts {
  if (order.fr) {
    const fr = order.fr;
    const range = mlaPageRange(fr.page, fr.lastPage);
    const details = [
      `vol. ${fr.volume}`,
      fr.issue ? `no. ${fr.issue}` : "",
      mlaDate(fr.date),
      range.includes("–") ? `pp. ${range}` : `p. ${range}`,
      mlaUrl(order.url),
    ].filter(Boolean);
    return [italic("Federal Register"), plain(`, ${details.join(", ")}.`)];
  }
  if (order.cfr) {
    const details = [
      "title 3",
      order.cfr.year,
      `p. ${order.cfr.page}`,
      mlaUrl(order.url),
    ];
    return [plain(`Code of Federal Regulations, ${details.join(", ")}.`)];
  }
  return order.url ? [plain(`${mlaUrl(order.url)}.`)] : [];
}

function mlaOutput(order: Order, labels: ExecutiveOrderLabels): CitationOutput {
  const author = `United States, Executive Office of the President${
    order.president ? ` [${order.president}]` : ""
  }.`;
  const title = `Executive Order ${order.number}${
    order.title ? `: ${order.title}` : ""
  }`;
  const signed = mlaDate(parsePartialDate(order.signed));
  const parts: CitationParts = [
    plain(`${author} ${title}${closer(title)} `),
    plain(signed ? `${signed}. ` : ""),
    ...mlaContainer(order),
  ];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(parts) },
      {
        label: labels.inText,
        parts: [plain(`(United States, Executive Order ${order.number})`)],
      },
    ],
    note: labels.mlaNote,
  };
}

/** Chicago, following the Bluebook: the Federal Register printing, else the compilation. */
function chicagoNote(order: Order): string {
  const head = `Exec. Order No. ${order.legalNumber}`;
  let source = "";
  if (order.fr) {
    const date = fullDate(order.fr.date);
    source = `${order.fr.volume} Fed. Reg. ${groupDigits(order.fr.page)}${
      date ? ` (${date})` : ""
    }`;
  } else if (order.cfr) {
    source = `3 C.F.R. ${order.cfr.page} (${order.cfr.year})`;
  }
  const pieces = [head, source, order.url].filter(Boolean);
  return `${pieces.join(", ")}.`;
}

export function executiveOrderIsReady(values: LegalValues): boolean {
  const order = readOrder(values);
  return order.number !== "" && (order.fr !== null || order.cfr !== null);
}

export function formatExecutiveOrder(
  values: LegalValues,
  labels: ExecutiveOrderLabels
): CitationOutput[] {
  const order = readOrder(values);
  return [
    apaOutput(order, labels),
    mlaOutput(order, labels),
    {
      key: "chicago",
      label: labels.chicago,
      entries: [{ label: labels.footnote, parts: [plain(chicagoNote(order))] }],
      note: labels.chicagoNote,
    },
  ];
}
